import { isEqual } from 'lodash';
import { useSyncExternalStore } from 'react';

import { IApplySelectedSpaJobProgress, IApplySelectedSpaJobResult } from 'quotes/types';

export interface IFinishedApplySelectedSpaJob {
  agreementCode: string;
  isStopped: boolean;
  result: IApplySelectedSpaJobResult;
}

// Module-level: several hosts mount per quote, a job outlives its host, and the BE does not
// de-duplicate jobs.
const pendingQuoteIds = new Set<number>();
const progressByQuoteId = new Map<number, IApplySelectedSpaJobProgress>();
// The results of a job whose host was gone when it ended, for the next host of that quote.
const parkedResultByQuoteId = new Map<number, IFinishedApplySelectedSpaJob>();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const notify = () => listeners.forEach((listener) => listener());

export const isApplySelectedSpaJobPending = (quoteId?: number | null): boolean =>
  !!quoteId && pendingQuoteIds.has(quoteId);

export const setApplySelectedSpaJobPending = (quoteId: number, isPending: boolean) => {
  if (isPending) {
    pendingQuoteIds.add(quoteId);
  } else {
    pendingQuoteIds.delete(quoteId);
  }

  progressByQuoteId.delete(quoteId);
  notify();
};

export const getApplySelectedSpaJobProgress = (
  quoteId?: number | null
): IApplySelectedSpaJobProgress | null => (quoteId && progressByQuoteId.get(quoteId)) || null;

// Cleared with the pending flag.
export const setApplySelectedSpaJobProgress = (
  quoteId: number,
  progress: IApplySelectedSpaJobProgress
) => {
  if (!isApplySelectedSpaJobPending(quoteId) || isEqual(progressByQuoteId.get(quoteId), progress)) {
    return;
  }

  progressByQuoteId.set(quoteId, progress);
  notify();
};

export const useIsApplySelectedSpaJobPending = (quoteId?: number | null): boolean =>
  useSyncExternalStore(subscribe, () => isApplySelectedSpaJobPending(quoteId));

export const useApplySelectedSpaJobProgress = (
  quoteId?: number | null
): IApplySelectedSpaJobProgress | null =>
  useSyncExternalStore(subscribe, () => getApplySelectedSpaJobProgress(quoteId));

export const parkApplySelectedSpaJobResult = (
  quoteId: number,
  finished: IFinishedApplySelectedSpaJob
) => {
  parkedResultByQuoteId.set(quoteId, finished);
  notify();
};

// Hands the parked results to one host only: whoever takes them shows them, once.
export const takeApplySelectedSpaJobResult = (
  quoteId?: number | null
): IFinishedApplySelectedSpaJob | null => {
  const parked = (quoteId && parkedResultByQuoteId.get(quoteId)) || null;

  if (parked) {
    parkedResultByQuoteId.delete(quoteId);
    notify();
  }

  return parked;
};

export const useHasParkedApplySelectedSpaJobResult = (quoteId?: number | null): boolean =>
  useSyncExternalStore(subscribe, () => !!quoteId && parkedResultByQuoteId.has(quoteId));
